import http from '@/utils/http'
import { WORD_BASEURL } from '@/utils/http'

export interface LexiconInfoBean {
  id: number
  title: string
  icon: string
  lexiconList: LexiconListBean[]
}

export interface LexiconListBean {
  id: number
  typeId: number
  name: string
  imgUrl: string
  practice: number
  sort: number
  wordCount: number
  createTime: number
  userLexiconInfo: UserLexiconInfoBean
  modeIds: number[]
}

export interface UserLexiconInfoBean {
  total: number
  masterCount: number
  learningCount: number
  errorCount: number
  collectCount: number
  reviewCount: number
  reviewSum: number
}

export interface IWordTranslationPart {
  part: string
  translation: string
}

export interface ExampleListBean {
  id: number
  wordId: number
  english: string
  chinese: string
  audio: string
}

export interface WordDetailBean {
  id: number
  word: string
  translation: string
  phoneticAm: string
  audioAm: string
  phoneticEm: string
  audioEm: string
  isCollection: number
  wordStatus: number
  exampleList: ExampleListBean[]
  isMaster?: number
}

export interface DetailWordListBean {
  groupId: number
  words: WordDetailBean[]
  groupNum: number
  lexiconId: number
}

export interface ReviewGroupBean {
  lexiconId: number
  groupNum: number
  status: number
}

export interface PracticeModeBean {
  id: number
  name: string
  icon: string
  iconUnchecked: string
  title: string
}

export function translationPartConverter(translation: string): IWordTranslationPart[] {
  if (!translation) {
    return []
  }
  const array = translation.split('\n')
  const parts: IWordTranslationPart[] = []
  const partRegex =
    /^((n\.|pron\.|adj\.|adv\.|vt\.|vi\.|v\.|prep\.|conj\.|art\.|num\.|int\.|abbr\.|modal|link-v|【[\u4e00-\u9fa5]+】)\s*)/i
  array.forEach((item) => {
    if (!item.trim()) {
      return
    }
    const match = item.match(partRegex)
    if (match) {
      const part = match[1].trim()
      const translationContent = item.slice(match[0].length).trim()
      parts.push({ part, translation: translationContent })
    } else {
      parts.push({ part: '', translation: item.trim() })
    }
  })
  return parts
}

/**
 * 获取全部词库列表
 * @returns
 */
export const getAllLexiconApi = () => {
  return http.get<LexiconInfoBean[]>(`${WORD_BASEURL}/api/lexicon/all`)
}

/**
 * 获取学习单词列表
 * @param lexiconId 词库ID
 * @returns
 */
export const getStudyWordListApi = (lexiconId: number) => {
  return http.get<DetailWordListBean>(`${WORD_BASEURL}/api/lexicon/word/study`, {
    lexiconId
  })
}

/**
 * 获取分组单词列表
 * @param lexiconId 词库ID
 * @param groupNum 组号
 * @returns
 */
export const getGroupWordListApi = (lexiconId: number, groupNum: string) => {
  return http.get<DetailWordListBean>(`${WORD_BASEURL}/api/lexicon/word/study`, {
    lexiconId,
    groupNum
  })
}

/**
 * 获取复习组列表
 * @param lexiconId 词库ID
 * @returns
 */
export const getReviewGroupListApi = (lexiconId: number) => {
  return http.get<ReviewGroupBean[]>(`${WORD_BASEURL}/api/lexicon/word/group`, {
    lexiconId
  })
}

/**
 * 批量更新单词状态
 * @param lexiconId 词库ID
 * @param wordIds 单词ID数组
 * @param wordStatus 状态：1已学 2已掌握 3错误 4不知道
 * @returns
 */
export const updateBatchWordStatusApi = (
  lexiconId: number,
  wordIds: number[],
  wordStatus: number
) => {
  return http.post<UserLexiconInfoBean>(`${WORD_BASEURL}/api/lexicon/update/batch`, {
    lexiconId,
    wordIds,
    wordStatus
  })
}

/**
 * 收藏/取消收藏单词
 * @param lexiconId 词库ID
 * @param wordId 单词ID
 * @returns
 */
export const toggleCollectWordApi = (lexiconId: number, wordId: number) => {
  return http.postForm(`${WORD_BASEURL}/api/lexicon/word/collect/save`, {
    lexiconId,
    wordId
  })
}

/**
 * 切换用户当前学习的词库
 * @param lexiconId 词库ID
 * @returns
 */
export const selectLexiconApi = (lexiconId: number) => {
  return http.postForm(`${WORD_BASEURL}/api/lexicon/user`, {
    lexiconId
  })
}

/**
 * 获取背词模式列表
 * @returns
 */
export const getModeListApi = () => {
  return http.get<PracticeModeBean[]>(`${WORD_BASEURL}/api/mode/list`)
}

/**
 * 获取单词类型列表
 * @param lexiconId 词库ID
 * @param type 类型：0全部 1已学 2掌握 3收藏 4错词
 * @param page 页码
 * @param perPage 每页数量
 * @returns
 */
export const getWordTypeListApi = (
  lexiconId: number,
  type: number,
  page: number,
  perPage: number
) => {
  return http.get<WordDetailBean[]>(`${WORD_BASEURL}/api/lexicon/word/all`, {
    lexiconId,
    type,
    page,
    perPage
  })
}

/**
 * 获取单词服务登录态（主站接口，返回 WordAppToken）
 */
export const getUserTokenByPhoneApi = () => {
  return http.get<string>('/api/auth/getUserTokenByPhone')
}

export interface WordUserInfoBean {
  isClock: boolean
  lexicon: LexiconListBean
}

/**
 * 获取用户信息（单词服务域名），包含 isClock、lexicon 等单词 app 专用字段
 */
export const getWordUserInfoApi = () => {
  return http.get<WordUserInfoBean>(`${WORD_BASEURL}/api/auth/getUser`)
}
